import { useMemo } from "react";
import { clamp, hash01 } from "./chunk-0.jsx";
export function OledPixelEye({
  cols,
  rows,
  size,
  on,
  pattern,
  side,
  blinkPulse,
  seedKey,
  gazeAnim = 'oledGaze'
}) {
  const isLeft = side === 'left';
  const seed = hash01(seedKey || `oled-pixel:${side}`);
  const blinkDurationS = 2.8 + seed * 2.0;
  const gazeDurationS = 3.6 + seed * 2.2;
  const blinkDuration = `${blinkDurationS.toFixed(2)}s`;
  const gazeDuration = `${gazeDurationS.toFixed(2)}s`;
  const blinkDelay = `${(-seed * blinkDurationS).toFixed(2)}s`;
  const gazeDelay = `${(-seed * gazeDurationS).toFixed(2)}s`;
  const cells = useMemo(() => {
    const set = new Set();
    const midX = Math.floor(cols / 2);
    const midY = Math.floor(rows / 2);
    const put = (x, y) => {
      if (x < 0 || y < 0 || x >= cols || y >= rows) return;
      set.add(`${x},${y}`);
    };
    if (pattern.kind === 'dash') {
      for (let x = 2; x < cols - 2; x++) put(x, midY);
    } else if (pattern.kind === 'dashDown') {
      const y = clamp(midY + 1, 0, rows - 1);
      for (let x = 2; x < cols - 2; x++) put(x, y);
    } else if (pattern.kind === 'bar') {
      for (let y = 1; y <= 2; y++) {
        for (let x = 1; x < cols - 1; x++) put(x, y);
      }
      for (let x = 2; x < cols - 2; x++) put(x, 3);
    } else if (pattern.kind === 'dots') {
      put(midX - 1, midY);
      put(midX, midY);
      put(midX - 1, midY - 1);
      put(midX, midY - 1);
    } else if (pattern.kind === 'chevron') {
      for (let x = 1; x < cols - 1; x++) {
        const dx = Math.abs(x - midX);
        const y = clamp(midY - 1 + Math.round(dx * 0.5), 0, rows - 1);
        put(x, y);
        put(x, clamp(y + 1, 0, rows - 1));
      }
    } else if (pattern.kind === 'zigzag') {
      const phase = isLeft ? 0 : 1;
      for (let x = 1; x < cols - 1; x++) {
        const y = clamp(midY + (x + phase) % 2, 0, rows - 1);
        put(x, y);
      }
    } else if (pattern.kind === 'wink') {
      if (isLeft) {
        for (let x = 2; x < cols - 2; x++) put(x, midY);
      } else {
        for (let y = 0; y < rows; y++) {
          for (let x = 0; x < cols; x++) {
            const dx = (x - midX) / (cols / 2);
            const dy = (y - midY) / (rows / 2);
            if (dx * dx + dy * dy < 0.55) put(x, y);
          }
        }
      }
    } else if (pattern.kind === 'x') {
      for (let x = 1; x < cols - 1; x++) {
        const y1 = Math.round((rows - 1) * (x / (cols - 1)));
        const y2 = rows - 1 - y1;
        put(x, y1);
        put(x, y2);
      }
    } else if (pattern.kind === 'half') {
      for (let y = 2; y < rows - 1; y++) {
        for (let x = 1; x < cols - 1; x++) put(x, y);
      }
    } else if (pattern.kind === 'slantSoft') {
      const sign = isLeft ? -1 : 1;
      for (let x = 1; x < cols - 1; x++) {
        const y = clamp(midY + Math.round((x - midX) * 0.18 * sign), 0, rows - 1);
        put(x, y);
      }
    } else if (pattern.kind === 'round') {
      for (let y = 0; y < rows; y++) {
        for (let x = 0; x < cols; x++) {
          const dx = (x - midX) / (cols / 2);
          const dy = (y - midY) / (rows / 2);
          if (dx * dx + dy * dy < 0.7) put(x, y);
        }
      }
    } else if (pattern.kind === 'heart') {
      const left = [[midX - 2, midY - 1], [midX - 3, midY], [midX - 2, midY + 1], [midX - 1, midY + 2], [midX, midY + 1]];
      const right = left.map(([x, y]) => [midX + (midX - x), y]);
      for (const [x, y] of [...left, ...right]) put(x, y);
      put(midX, midY + 3);
    } else if (pattern.kind === 'slant') {
      const sign = isLeft ? -1 : 1;
      for (let x = 1; x < cols - 1; x++) {
        const y = clamp(midY + Math.round((x - midX) * 0.3 * sign), 0, rows - 1);
        put(x, y);
        put(x, clamp(y + 1, 0, rows - 1));
      }
    } else {
      for (let y = 1; y < rows - 1; y++) {
        for (let x = 1; x < cols - 1; x++) put(x, y);
      }
    }
    return [...set].map(k => {
      const [x, y] = k.split(',').map(n => Number(n));
      return {
        x,
        y
      };
    });
  }, [cols, rows, pattern.kind, isLeft]);
  const w = cols * size;
  const h = rows * size;
  return <div className="relative w-[28%] h-[58%] flex items-center justify-center">
      <div className="grid" style={{
      gridTemplateColumns: `repeat(${cols}, ${size}px)`,
      width: w,
      height: h,
      animationName: gazeAnim,
      animationTimingFunction: 'ease-in-out',
      animationIterationCount: 'infinite',
      animationDuration: gazeDuration,
      animationDelay: gazeDelay
    }}>
        {Array.from({
        length: cols * rows
      }).map((_, i) => {
        const x = i % cols;
        const y = Math.floor(i / cols);
        const active = cells.some(c => c.x === x && c.y === y);
        return <div key={`${x}-${y}`} className={`${active ? on : ''}`} style={{
          width: size,
          height: size
        }} />;
      })}
      </div>
      <div className="absolute inset-0 bg-black origin-center pointer-events-none" style={{
      transform: 'scaleY(0)',
      animationName: 'oledBlink',
      animationTimingFunction: 'ease-in-out',
      animationIterationCount: 'infinite',
      animationDuration: blinkDuration,
      animationDelay: blinkDelay
    }} />
      <div className={`absolute inset-0 bg-black origin-center pointer-events-none transition-transform duration-150 ease-in-out ${blinkPulse ? 'scale-y-100' : 'scale-y-0'}`} />
    </div>;
}
